/**
 * LensScoreCard - Governance Lens Score Display Card
 * ============================================================================
 * DC-1045: Card component displaying lens analysis score with gauge and findings
 *
 * Features:
 * - Lens icon and name header
 * - ScoreGauge component (circular gauge)
 * - Coverage progress bar
 * - Findings badge (red if critical > 0)
 * - Click expands or navigates
 *
 * Pattern: Follows FDACredibilityCard.tsx (DC-930)
 *
 * @version 1.0.0
 * @created 2026-01-25
 * ============================================================================
 */

import React from 'react';
import { ScoreGauge } from '../../vendor/ScoreGauge';
import {
  type GovernanceLensId,
  type LensResult,
  type FindingsSummary,
  LENS_CONFIG,
  SEVERITY_CONFIG,
  getScoreColor,
} from './types';
import './LensScoreCard.css';

// CORRECTNESS: the server can supply a lensId (e.g. 'agent_governance') that has
// no LENS_CONFIG entry; the unguarded LENS_CONFIG[lensId] then returned
// undefined and config.bgColor crashed the Governance Document Detail page. This
// neutral fallback renders the card safely for any unknown lens. lensConfigFor()
// is used at every config lookup site below.
const FALLBACK_LENS_CONFIG = {
  name: 'Governance Lens',
  shortName: 'Lens',
  icon: 'G',
  color: '#6B7280',
  bgColor: '#F3F4F6',
};

function lensConfigFor(lensId: GovernanceLensId): typeof FALLBACK_LENS_CONFIG {
  return lensId in LENS_CONFIG ? LENS_CONFIG[lensId] : FALLBACK_LENS_CONFIG;
}

// ============================================================================
// Types
// ============================================================================

export interface LensScoreCardProps {
  /** Lens identifier */
  lensId: GovernanceLensId;
  /** Lens result data (null if not analyzed) */
  result: LensResult | null;
  /** Score override (use when result is not available) */
  score?: number;
  /** Findings summary override */
  findingsSummary?: FindingsSummary;
  /** Click handler */
  onClick?: () => void;
  /** Compact mode for smaller spaces */
  compact?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Whether lens is selected */
  selected?: boolean;
  /** Show as disabled/not-analyzed */
  disabled?: boolean;
  /** Custom className */
  className?: string;
}

// ============================================================================
// Helper Components
// ============================================================================

interface FindingsBadgeProps {
  summary: FindingsSummary;
  compact?: boolean;
}

const FindingsBadge: React.FC<FindingsBadgeProps> = ({ summary, compact }) => {
  if (summary.total === 0) {
    return (
      <span className="lsc-findings-badge lsc-findings-badge--none">
        No findings
      </span>
    );
  }

  const hasCritical = summary.critical > 0;
  const hasMajor = summary.major > 0;

  if (compact) {
    return (
      <span
        className={`lsc-findings-badge lsc-findings-badge--compact ${
          hasCritical ? 'lsc-findings-badge--critical' : hasMajor ? 'lsc-findings-badge--major' : ''
        }`}
      >
        {summary.total}
      </span>
    );
  }

  return (
    <div className="lsc-findings-badges">
      {summary.critical > 0 && (
        <span
          className="lsc-findings-badge"
          style={{
            backgroundColor: SEVERITY_CONFIG.critical.bgColor,
            color: SEVERITY_CONFIG.critical.color,
          }}
        >
          {summary.critical} Critical
        </span>
      )}
      {summary.major > 0 && (
        <span
          className="lsc-findings-badge"
          style={{
            backgroundColor: SEVERITY_CONFIG.major.bgColor,
            color: SEVERITY_CONFIG.major.color,
          }}
        >
          {summary.major} Major
        </span>
      )}
      {summary.minor + summary.observation > 0 && !hasCritical && !hasMajor && (
        <span className="lsc-findings-badge lsc-findings-badge--minor">
          {summary.minor + summary.observation} Minor
        </span>
      )}
    </div>
  );
};

interface CoverageBarProps {
  covered: number;
  total: number;
  compact?: boolean;
}

const CoverageBar: React.FC<CoverageBarProps> = ({ covered, total, compact }) => {
  const percentage = total > 0 ? Math.round((covered / total) * 100) : 0;

  return (
    <div className={`lsc-coverage ${compact ? 'lsc-coverage--compact' : ''}`}>
      <div className="lsc-coverage__header">
        <span
          className="lsc-coverage__label"
          title="Tier 1: keyword/pattern coverage — an indicative presence check, not an assessed compliance score. Run Deep Analysis for requirement-by-requirement compliance."
        >
          Keyword coverage (indicative)
        </span>
        <span className="lsc-coverage__value">
          {covered}/{total} concepts
        </span>
      </div>
      <div className="lsc-coverage__bar-bg">
        <div
          className="lsc-coverage__bar-fill"
          style={{
            width: `${percentage}%`,
            backgroundColor: getScoreColor(percentage),
          }}
        />
      </div>
    </div>
  );
};

// ============================================================================
// Loading State
// ============================================================================

const LoadingState: React.FC<{ compact?: boolean; lensId: GovernanceLensId }> = ({
  compact,
  lensId,
}) => {
  const config = lensConfigFor(lensId);

  return (
    <div className={`lsc-card lsc-card--loading ${compact ? 'lsc-card--compact' : ''}`}>
      <div className="lsc-card__header">
        <span
          className="lsc-card__icon"
          style={{ backgroundColor: config.bgColor, color: config.color }}
        >
          {config.icon}
        </span>
        <span className="lsc-card__name">{config.shortName}</span>
      </div>
      <div className="lsc-card__body">
        <div className="lsc-loading-skeleton lsc-loading-skeleton--gauge" />
        <div className="lsc-loading-skeleton lsc-loading-skeleton--text" />
      </div>
    </div>
  );
};

// ============================================================================
// Empty State
// ============================================================================

const EmptyState: React.FC<{
  lensId: GovernanceLensId;
  compact?: boolean;
  onClick?: () => void;
  disabled?: boolean;
}> = ({ lensId, compact, onClick, disabled }) => {
  const config = lensConfigFor(lensId);

  return (
    <div
      className={`lsc-card lsc-card--empty ${compact ? 'lsc-card--compact' : ''} ${
        disabled ? 'lsc-card--disabled' : ''
      } ${onClick ? 'lsc-card--clickable' : ''}`}
      onClick={!disabled ? onClick : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick && !disabled ? 0 : undefined}
    >
      <div className="lsc-card__header">
        <span
          className="lsc-card__icon"
          style={{
            backgroundColor: disabled ? '#F3F4F6' : config.bgColor,
            color: disabled ? '#9CA3AF' : config.color,
          }}
        >
          {config.icon}
        </span>
        <span className="lsc-card__name">{config.shortName}</span>
      </div>
      <div className="lsc-card__body lsc-card__body--empty">
        <span className="lsc-card__score-placeholder">--</span>
        <span className="lsc-card__empty-text">Not analyzed</span>
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const LensScoreCard: React.FC<LensScoreCardProps> = ({
  lensId,
  result,
  score: scoreProp,
  findingsSummary: summaryProp,
  onClick,
  compact = false,
  loading = false,
  selected = false,
  disabled = false,
  className = '',
}) => {
  const config = lensConfigFor(lensId);

  // Loading state
  if (loading) {
    return <LoadingState lensId={lensId} compact={compact} />;
  }

  // Empty state (not analyzed)
  const effectiveScore = result?.score ?? scoreProp;
  if (effectiveScore === undefined || effectiveScore === null) {
    return (
      <EmptyState
        lensId={lensId}
        compact={compact}
        onClick={onClick}
        disabled={disabled}
      />
    );
  }

  // Build findings summary
  const findingsSummary: FindingsSummary = summaryProp || {
    critical: result?.findings?.filter(f => f.severity === 'critical').length || 0,
    major: result?.findings?.filter(f => f.severity === 'major').length || 0,
    minor: result?.findings?.filter(f => f.severity === 'minor').length || 0,
    observation: result?.findings?.filter(f => f.severity === 'observation').length || 0,
    total: result?.findings?.length || 0,
  };

  const hasCritical = findingsSummary.critical > 0;

  return (
    <div
      className={`lsc-card ${compact ? 'lsc-card--compact' : ''} ${
        selected ? 'lsc-card--selected' : ''
      } ${onClick ? 'lsc-card--clickable' : ''} ${
        hasCritical ? 'lsc-card--critical' : ''
      } ${className}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
    >
      {/* Header */}
      <div className="lsc-card__header">
        <span
          className="lsc-card__icon"
          style={{ backgroundColor: config.bgColor, color: config.color }}
        >
          {config.icon}
        </span>
        <span className="lsc-card__name">{compact ? config.shortName : config.name}</span>
        {hasCritical && (
          <span className="lsc-card__alert-icon" title="Critical findings">
            ⚠
          </span>
        )}
      </div>

      {/* Body */}
      <div className="lsc-card__body">
        {/* Score Gauge */}
        <div className="lsc-card__gauge">
          <ScoreGauge
            score={effectiveScore}
            size={compact ? 'sm' : 'md'}
            variant="full"
            showValue={true}
            showPercentSign={false}
            animated={true}
          />
        </div>

        {/* Coverage */}
        {result && !compact && (
          <CoverageBar
            covered={result.concepts_covered?.length || 0}
            total={result.total_concepts || 0}
            compact={compact}
          />
        )}

        {/* Findings Summary */}
        <FindingsBadge summary={findingsSummary} compact={compact} />
      </div>

      {/* Footer with timestamp */}
      {result?.analyzed_at && !compact && (
        <div className="lsc-card__footer">
          <span className="lsc-card__timestamp">
            {new Date(result.analyzed_at).toLocaleDateString()}
          </span>
        </div>
      )}

      {/* Selection indicator */}
      {selected && <div className="lsc-card__selection-indicator" />}
    </div>
  );
};

export default LensScoreCard;
